/*
  Component: Collection Sort — dismissal
  Responsibility: close the open sort panel when the shopper clicks away from it or presses
                  Escape. Nothing else. Which order is applied, and refetching the grid
                  when it changes, is Dawn's — see snippets/kl-collection-sort.liquid.

  <details> gives us open and close on the summary, keyboard operation and focus for free.
  The two things it does not do are dismiss on an outside click and dismiss on Escape, so
  those are the only two things here.

  Dawn covers Escape for its own facets by binding onKeyUpEscape to #FacetsWrapperDesktop
  (assets/facets.js:14). The sort renders inside #FacetSortDrawerForm instead, which that
  binding never reaches, so it is not covered and is handled below.

  Both listeners are on the document rather than on the control. That was originally done
  because facets.js was thought to replace this markup on every change; it does not — see
  the note in snippets/kl-collection-sort.liquid, which sets out where that was checked.
  Delegation is still the right shape for a control that opens and closes, and it costs
  nothing, so it stands.

  There are three ways out of an open panel and all three are here: a click outside it, the
  Escape key, and picking an order. Only the last was ever in doubt — an earlier comment had
  it that facets.js redrew the control shut after a choice, which it does not.

  What is deliberately not here is keeping the two sort controls showing the same order.
  That is assets/kl-collection-sort-sync.js. This file only ever closes a panel.
*/

const SORT_CONTROL_SELECTOR = '[data-kl-collection-sort]';
const OPEN_SORT_CONTROL_SELECTOR = '[data-kl-collection-sort][open]';
const SORT_OPTION_SELECTOR = '[data-kl-sort-option]';

/*
  Pure: which of the open controls a click landed outside of. DOM-free apart from the
  containment test it is handed, so the decision is testable without a browser.
*/
function getControlsToClose(openControls, isInsideControl) {
  return openControls.filter((control) => !isInsideControl(control));
}

function closeSortControl(control) {
  control.removeAttribute('open');

  const summary = control.querySelector('summary');

  if (!summary) return;

  summary.setAttribute('aria-expanded', 'false');
}

function getOpenSortControls() {
  return Array.from(document.querySelectorAll(OPEN_SORT_CONTROL_SELECTOR));
}

/*
  The control the shopper has just picked an order in, or nothing if the click landed
  somewhere else. The drawer's sort accordion carries the same options and is deliberately
  not matched: it has no [data-kl-collection-sort], because an accordion inside a drawer
  stays open where a dropdown over the grid does not.
*/
function getChosenSortControl(event) {
  const option = event.target.closest(SORT_OPTION_SELECTOR);

  return option ? option.closest(SORT_CONTROL_SELECTOR) : null;
}

/*
  One pass over one click. A click either lands outside an open panel, which closes it, or
  on an order inside one, which also closes it — the shopper has said what they came to say.

  On a click and not on a change, and the difference matters for the keyboard. Arrow keys
  move through a radio group and check each one they land on, firing change every time, so
  closing on change would shut the panel on the first press and leave a keyboard shopper
  unable to reach the rest of the list. Escape is their way out, and it is handled below.
*/
function handleDocumentClick(event) {
  try {
    const openControls = getOpenSortControls();

    if (!openControls.length) return;

    const chosenControl = getChosenSortControl(event);

    if (chosenControl) {
      closeSortControl(chosenControl);
      return;
    }

    const toClose = getControlsToClose(openControls, (control) => control.contains(event.target));

    toClose.forEach(closeSortControl);
  } catch (error) {
    console.debug('kl-collection-sort: could not close on outside click', { error });
  }
}

/*
  Focus goes back to the summary, which is where it would have been had the panel never
  opened. Without that a keyboard user is left on an element that has just been hidden and
  the next Tab starts from the top of the document.
*/
function handleDocumentKeyUp(event) {
  if (event.key !== 'Escape') return;

  try {
    const openControls = getOpenSortControls();

    if (!openControls.length) return;

    openControls.forEach((control) => {
      closeSortControl(control);

      const summary = control.querySelector('summary');

      if (summary) summary.focus();
    });
  } catch (error) {
    console.debug('kl-collection-sort: could not close on Escape', { error });
  }
}

document.addEventListener('click', handleDocumentClick);
document.addEventListener('keyup', handleDocumentKeyUp);
